import React from 'react';
import { MapPin, Phone, ArrowRight } from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SectionHeader } from '../../components/common/SectionHeader';
import { COMPANY_INFO } from '../../data/siteData';
import { SEO } from '../../components/common/SEO';

export default function CompanyPage() {
  const rows = [
    { label: '商号', value: `${COMPANY_INFO.name}（英文表記：${COMPANY_INFO.nameEn}）` },
    { label: '法人番号', value: COMPANY_INFO.corporateNumber },
    { label: '代表者', value: COMPANY_INFO.representative },
    {
      label: '創業・設立',
      value: `${COMPANY_INFO.founded}創業 ／ ${COMPANY_INFO.established}設立（${COMPANY_INFO.foundedNote}）`,
    },
    { label: '資本金', value: COMPANY_INFO.capital },
    { label: '所在地', value: COMPANY_INFO.address },
    { label: '電話番号', value: `${COMPANY_INFO.tel}（${COMPANY_INFO.hours}）` },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="会社情報｜代表メッセージ・会社概要・沿革｜Spaq株式会社"
        description="Spaq株式会社の企業姿勢、代表メッセージ、これまでの実績基盤、会社概要、2009年の創業からの歩み（沿革）、所在地をご案内します。"
        canonicalPath="/company"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Badge variant="cyan" className="mb-4">COMPANY PROFILE</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
              会社情報
            </h1>
            <p className="mt-4 text-base sm:text-lg text-navy-muted leading-relaxed max-w-3xl">
              2009年の創業、2012年の法人設立を経て、2022年にSpaq株式会社へ社名を変更しました。数多くの施設現場で培ってきた経験をもとに、企業のエネルギー課題に誠実に向き合っています。
            </p>
          </div>
        </section>

        {/* Section 1: 代表メッセージ（旧「SPAQについて」と「会社情報」を統合・再構成） */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <SectionHeader
              tag="MESSAGE"
              title="代表メッセージ"
              subtitle="現場第一の知見と技術で、お客様一人ひとりの電力課題に向き合い続けます。"
            />

            <div className="rounded-3xl border border-surface-border bg-surface-warm/60 p-7 sm:p-11 shadow-soft space-y-5">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-navy leading-snug">
                「設備を納めて終わり」ではなく、
                <br />
                電気が賢く使われ続ける仕組みをつくる。
              </h2>
              <p className="text-sm sm:text-base text-navy-muted leading-relaxed">
                2009年にトライデント・フレームワークスを創業し、同時期から太陽光発電の設計・施工を手掛けてまいりました。2012年に株式会社トライデント・フレームワークスを設立し、2022年にSpaq株式会社へ社名を変更しました。
              </p>
              <p className="text-sm sm:text-base text-navy-muted leading-relaxed">
                時代が「売電」から「自家消費」へと大きく変わる中で、私たちが現場で痛感してきたのは、どれほど優れたパネルや蓄電池であっても、その施設の電気の使い方に合っていなければ無駄が生まれるということです。また、導入後に機器同士がバラバラに動いてしまえば、現場で働く方々に暑さ・寒さの我慢を強いることになってしまいます。
              </p>
              <p className="text-sm sm:text-base text-navy-muted leading-relaxed">
                だからこそ私たちは、単なる設備販売や工事にとどまらず、導入前の正直な相性診断から、最適な設備設計・施工、そして導入後の無理のない協調運用までを一貫して支える体制を築いてきました。これからも現場第一の知見と技術開発を重ね、お客様と社会の持続的なエネルギー活用に貢献してまいります。
              </p>
              <div className="pt-5 border-t border-spaq-gold/30 flex items-center justify-end">
                <div className="text-right">
                  <div className="text-xs text-navy-muted">{COMPANY_INFO.name}</div>
                  <div className="text-lg font-bold text-navy">{COMPANY_INFO.representative}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: 数字で見るSPAQの現場基盤 */}
        <section className="py-14 md:py-20 bg-surface-slate border-y border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <SectionHeader
              tag="TRACK RECORD"
              title="数字で見るSPAQの現場基盤"
              subtitle="長年にわたり積み重ねてきた設計・施工の現場経験が、現在のソフトウェア開発と制御設計の土台となっています。"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {COMPANY_INFO.trackRecordSummary.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-3xl border border-surface-border p-6 sm:p-7 shadow-soft flex flex-col justify-between"
                >
                  <div>
                    <div className="text-xs font-bold text-navy-muted mb-2">{item.label}</div>
                    <div className="flex items-baseline gap-1 my-2">
                      <span className="text-3xl sm:text-4xl font-extrabold text-navy font-mono tracking-tight">
                        {item.value}
                      </span>
                      <span className="text-sm font-bold text-spaq-cyan-dark">{item.unit}</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-navy-muted leading-relaxed mt-3 pt-3 border-t border-surface-border">
                    {item.note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: 会社概要テーブル */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <SectionHeader tag="OVERVIEW" title="会社概要" />

            <div className="rounded-3xl border border-surface-border overflow-hidden bg-white shadow-soft">
              <dl className="divide-y divide-surface-border">
                {rows.map((row, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 sm:grid-cols-12 p-5 sm:p-6 gap-2 sm:gap-4"
                  >
                    <dt className="sm:col-span-4 text-sm font-bold text-navy">
                      {row.label}
                    </dt>
                    <dd className="sm:col-span-8 text-sm text-navy-muted leading-relaxed">
                      {row.value}
                    </dd>
                  </div>
                ))}

                <div className="grid grid-cols-1 sm:grid-cols-12 p-5 sm:p-6 gap-2 sm:gap-4">
                  <dt className="sm:col-span-4 text-sm font-bold text-navy">
                    主な事業内容
                  </dt>
                  <dd className="sm:col-span-8 text-sm text-navy-muted">
                    <ul className="space-y-2">
                      {COMPANY_INFO.businessItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-spaq-cyan-dark font-bold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        {/* Section 4: 沿革 */}
        <section className="py-16 md:py-24 bg-surface-slate border-t border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <SectionHeader
              tag="HISTORY"
              title="沿革（SPAQの歩み）"
              subtitle="2009年の創業、2012年の法人設立、2022年の社名変更を経て、現場知見をソフトウェア・制御技術へと発展させてきました。"
            />

            <div className="space-y-4">
              {COMPANY_INFO.history.map((h, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-surface-border bg-white p-6 flex flex-col sm:flex-row sm:items-start gap-4 shadow-soft"
                >
                  <div className="sm:w-32 shrink-0">
                    <span className="inline-block px-3 py-1 rounded-full bg-spaq-cyan-pale text-spaq-cyan-dark font-mono font-bold text-xs">
                      {h.year}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-navy mb-1.5">{h.title}</h3>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                      {h.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Access & Contact Box */}
            <div className="mt-14 rounded-3xl border border-surface-border bg-surface-sky p-7 sm:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-navy">所在地・お問い合わせ窓口</h3>
                <div className="flex items-center gap-2 text-sm text-navy-muted">
                  <MapPin className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                  <span>{COMPANY_INFO.address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-navy-muted">
                  <Phone className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                  <span>TEL：{COMPANY_INFO.tel}（{COMPANY_INFO.hours}）</span>
                </div>
              </div>
              <Button
                to="/contact"
                variant="primary"
                className="shrink-0"
                icon={<ArrowRight className="w-4 h-4" />}
              >
                お問い合わせフォームへ
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
